import {api} from './api.js';
import type {AuthSnapshot} from './auth.js';
import {apiUrl} from './config.js';
import {
  ENTERPRISE_SPACE_BLOCKS,
  ENTERPRISE_SPACE_NOTE_AUDIENCES,
  ENTERPRISE_SPACE_PAGE_ID,
  ENTERPRISE_SPACE_VIEWERS,
  createEnterpriseSpaceNoteId,
  normalizeEnterpriseSpaceLayout,
  type EnterpriseSpaceLayout,
  type EnterpriseSpaceNote,
} from '../common/clientPageLayout.js';
import {escapeHtml} from './layout.js';

const PAGE_CHOICES = [
  {id: ENTERPRISE_SPACE_PAGE_ID, title: '所属团队', href: '/admin/pages/enterprise-space', path: '/tax-assistant/enterprise-space', summary: '配置这一页的板块文案，以及每个板块对哪类用户显示。'},
];

/**
 * 后台「页面管理」。先选择页面，再进入对应编辑。
 * @param host 路由内容容器
 * @param auth 当前管理员
 */
export async function renderClientPagesAdmin(host: HTMLElement, auth: AuthSnapshot): Promise<void> {
  const pageId = window.location.pathname.replace(/\/$/, '').split('/').pop() || '';
  if (pageId === ENTERPRISE_SPACE_PAGE_ID) {
    await renderEnterpriseSpaceEditor(host, auth);
    return;
  }
  renderPageCatalog(host);
}

/**
 * 页面管理入口：选择要编辑的客户端页面。
 * @param host 路由内容容器
 */
function renderPageCatalog(host: HTMLElement): void {
  const cards = PAGE_CHOICES.map((item) => `<a class="page-layout-choice" href="${item.href}"><strong>${escapeHtml(item.title)}</strong><span>${escapeHtml(item.path)}</span><p>${escapeHtml(item.summary)}</p></a>`).join('');
  host.innerHTML = `<section class="admin-panel page-layout-panel"><div class="admin-list-heading"><div><h2>页面管理</h2><span>先选择要管理的页面</span></div></div><p class="panel-note">进入某个页面后，可以配置这一页里各个板块的文案，以及这些板块对哪类用户显示。保存作用在顶部所选环境。</p><div class="page-layout-catalog">${cards}</div></section>`;
}

/**
 * 所属团队页的板块编辑器。
 * @param host 路由内容容器
 * @param auth 当前管理员
 */
async function renderEnterpriseSpaceEditor(host: HTMLElement, auth: AuthSnapshot): Promise<void> {
  const canEdit = auth.role === 'super_admin';
  host.innerHTML = '<section class="admin-panel"><h2>所属团队</h2><p class="panel-note">正在加载…</p></section>';
  try {
    const payload = await api.get<{layout: EnterpriseSpaceLayout}>(apiUrl(`/api/admin/client-pages/${ENTERPRISE_SPACE_PAGE_ID}`));
    paint(host, normalizeEnterpriseSpaceLayout(payload.layout), canEdit);
  } catch (error) {
    host.innerHTML = `<section class="admin-panel"><h2>所属团队</h2><p class="panel-note">${escapeHtml(error instanceof Error ? error.message : String(error))}</p></section>`;
  }
}

/**
 * 画出所属团队页的板块编辑器。
 * @param host 路由内容容器
 * @param layout 当前草稿
 * @param canEdit 超级管理员可保存
 */
function paint(host: HTMLElement, layout: EnterpriseSpaceLayout, canEdit: boolean): void {
  const locked = canEdit ? '' : ' disabled';
  const blocks = ENTERPRISE_SPACE_BLOCKS.map((definition) => {
    const block = layout.blocks.find((item) => item.id === definition.id);
    const fields = definition.fields.map((field) => {
      const value = escapeHtml(block?.fields[field.key] ?? field.defaultValue);
      const control = field.multiline
        ? `<textarea data-block-field="${definition.id}:${field.key}" rows="3" maxlength="${field.maxLength}"${locked}>${value}</textarea>`
        : `<input data-block-field="${definition.id}:${field.key}" type="text" maxlength="${field.maxLength}" value="${value}"${locked}>`;
      return `<label class="page-layout-field"><span>${escapeHtml(field.label)}</span>${control}</label>`;
    }).join('');
    const audiences = ENTERPRISE_SPACE_VIEWERS.map((viewer) => `<label class="page-layout-visible"><input data-block-audience="${definition.id}" data-viewer="${viewer.id}" type="checkbox"${block?.audiences[viewer.id] ? ' checked' : ''}${locked}> ${escapeHtml(viewer.label)}</label>`).join('');
    return `<article class="page-layout-block"><header><div><h3>${escapeHtml(definition.label)}</h3><p>对哪些用户显示</p></div><div class="page-layout-audiences">${audiences}</div></header>${fields}</article>`;
  }).join('');
  const notes = layout.notes.map((note) => noteCard(note, canEdit)).join('') || '<p class="panel-note">还没有自定义说明。添加后可以选择谁能在所属团队页看到它。</p>';
  host.innerHTML = `<section class="admin-panel page-layout-panel"><div class="admin-list-heading"><div><a class="admin-back-link" href="/admin/pages">返回页面列表</a><h2>所属团队</h2><span>/tax-assistant/enterprise-space${layout.updatedAt ? ` · 最近保存 ${escapeHtml(new Date(layout.updatedAt).toLocaleString('zh-CN'))}` : ''}</span></div><div class="page-layout-actions">${canEdit ? '<button class="secondary-button" type="button" data-add-note>添加说明</button><button class="primary-button" type="button" data-save-layout>保存</button>' : ''}</div></div><p class="panel-note">每个板块都可以改文案，并勾选对尚未加入、普通成员、团队管理员、最高管理员是否显示。普通成员看到成员名单时只能查看资料，不能添加、移除或分配席位。增加、复制和删除只用于自定义说明。</p><p class="page-layout-status" data-layout-status role="status"></p><div class="page-layout-blocks">${blocks}</div><h3 class="page-layout-notes-title">自定义说明</h3><div class="page-layout-notes">${notes}</div></section>`;
  if (!canEdit) return;
  host.querySelector<HTMLButtonElement>('[data-add-note]')?.addEventListener('click', () => {
    const next = readDraft(host, layout.updatedAt);
    next.notes.push({id: createEnterpriseSpaceNoteId(next.notes.map((item) => item.id)), title: '', body: '', audience: 'joined', visible: true});
    paint(host, next, canEdit);
    host.querySelector<HTMLInputElement>('.page-layout-notes article:last-child [data-note-title]')?.focus();
  });
  host.querySelectorAll<HTMLButtonElement>('[data-copy-note]').forEach((button) => button.addEventListener('click', () => {
    const next = readDraft(host, layout.updatedAt);
    const index = next.notes.findIndex((item) => item.id === button.dataset.copyNote);
    const source = next.notes[index];
    if (!source) return;
    const copy: EnterpriseSpaceNote = {...source, id: createEnterpriseSpaceNoteId(next.notes.map((item) => item.id)), title: source.title ? `${source.title} 副本`.slice(0, 80) : '副本'};
    next.notes.splice(index + 1, 0, copy);
    paint(host, next, canEdit);
  }));
  host.querySelectorAll<HTMLButtonElement>('[data-delete-note]').forEach((button) => button.addEventListener('click', () => {
    const next = readDraft(host, layout.updatedAt);
    next.notes = next.notes.filter((item) => item.id !== button.dataset.deleteNote);
    paint(host, next, canEdit);
  }));
  host.querySelector<HTMLButtonElement>('[data-save-layout]')?.addEventListener('click', async () => {
    const status = host.querySelector<HTMLElement>('[data-layout-status]');
    const button = host.querySelector<HTMLButtonElement>('[data-save-layout]');
    const draft = readDraft(host, layout.updatedAt);
    if (button) button.disabled = true;
    if (status) status.textContent = '正在保存…';
    try {
      const saved = await api.put<{layout: EnterpriseSpaceLayout}>(apiUrl(`/api/admin/client-pages/${ENTERPRISE_SPACE_PAGE_ID}`), {layout: draft});
      paint(host, normalizeEnterpriseSpaceLayout(saved.layout), canEdit);
      const nextStatus = host.querySelector<HTMLElement>('[data-layout-status]');
    } catch (error) {
      if (button) button.disabled = false;
      if (status) status.textContent = error instanceof Error ? error.message : String(error);
    }
  });
}

/**
 * 一张自定义说明卡片。
 * @param note 说明
 * @param canEdit 是否允许修改
 */
function noteCard(note: EnterpriseSpaceNote, canEdit: boolean): string {
  const locked = canEdit ? '' : ' disabled';
  const options = ENTERPRISE_SPACE_NOTE_AUDIENCES.map((item) => `<option value="${item.id}"${item.id === note.audience ? ' selected' : ''}>${escapeHtml(item.label)}</option>`).join('');
  return `<article class="page-layout-block page-layout-note"><header><div><h3>自定义说明</h3></div><div class="page-layout-note-actions">${canEdit ? `<button class="secondary-button" type="button" data-copy-note="${escapeHtml(note.id)}">复制</button><button class="danger-button" type="button" data-delete-note="${escapeHtml(note.id)}">删除</button>` : ''}</div></header><label class="page-layout-visible"><input data-note-visible="${escapeHtml(note.id)}" type="checkbox"${note.visible ? ' checked' : ''}${locked}> 在客户端显示</label><label class="page-layout-field"><span>标题</span><input data-note-title="${escapeHtml(note.id)}" type="text" maxlength="80" value="${escapeHtml(note.title)}"${locked}></label><label class="page-layout-field"><span>正文</span><textarea data-note-body="${escapeHtml(note.id)}" rows="3" maxlength="1000"${locked}>${escapeHtml(note.body)}</textarea></label><label class="page-layout-field"><span>谁能看到</span><select data-note-audience="${escapeHtml(note.id)}"${locked}>${options}</select></label></article>`;
}

/**
 * 从编辑器读回草稿。空白说明会在保存时被丢掉。
 * @param host 路由内容容器
 * @param updatedAt 当前保存时间
 */
function readDraft(host: HTMLElement, updatedAt: string): EnterpriseSpaceLayout {
  const blocks = ENTERPRISE_SPACE_BLOCKS.map((definition) => {
    const fields: Record<string, string> = {};
    for (const field of definition.fields) {
      const control = host.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[data-block-field="${definition.id}:${field.key}"]`);
      fields[field.key] = control?.value ?? field.defaultValue;
    }
    const audiences = {
      unjoined: Boolean(host.querySelector<HTMLInputElement>(`[data-block-audience="${definition.id}"][data-viewer="unjoined"]`)?.checked),
      member: Boolean(host.querySelector<HTMLInputElement>(`[data-block-audience="${definition.id}"][data-viewer="member"]`)?.checked),
      admin: Boolean(host.querySelector<HTMLInputElement>(`[data-block-audience="${definition.id}"][data-viewer="admin"]`)?.checked),
      owner: Boolean(host.querySelector<HTMLInputElement>(`[data-block-audience="${definition.id}"][data-viewer="owner"]`)?.checked),
    };
    return {id: definition.id, visible: Object.values(audiences).some(Boolean), audiences, fields};
  });
  const notes: EnterpriseSpaceNote[] = [...host.querySelectorAll<HTMLElement>('[data-note-title]')].map((input) => {
    const id = input.dataset.noteTitle || '';
    const audience = host.querySelector<HTMLSelectElement>(`[data-note-audience="${CSS.escape(id)}"]`)?.value;
    return {
      id,
      title: input instanceof HTMLInputElement ? input.value : '',
      body: host.querySelector<HTMLTextAreaElement>(`[data-note-body="${CSS.escape(id)}"]`)?.value ?? '',
      audience: audience === 'unjoined' || audience === 'members' || audience === 'managers' || audience === 'owner' ? audience : 'joined',
      visible: Boolean(host.querySelector<HTMLInputElement>(`[data-note-visible="${CSS.escape(id)}"]`)?.checked),
    };
  });
  return {pageId: ENTERPRISE_SPACE_PAGE_ID, blocks, notes, updatedAt};
}
